import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import type { Activity, AthleteDetail, Coach, Group, TrainingPlan } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { ConversationPanel } from "../../components/ConversationPanel";
import { ProfileForm } from "../../components/ProfileForm";
import { ProfileSummary } from "../../components/ProfileSummary";
import {
  Badge,
  Button,
  ErrorState,
  FormError,
  Loading,
  Modal,
  PageHead,
  SelectField,
  StatusBadge,
  TextField,
  useToast,
} from "../../components/ui";
import { formatDate, formatDateTime, metersToKm } from "../../lib/format";
import { addDays, iso, todayIso } from "../../lib/training";
import { useAsync } from "../../lib/useAsync";
import { Link, useLocation } from "../../router/router";
import { t, tn } from "../../i18n";

function RecentActivities({ orgId, athleteId }: { orgId: string; athleteId: string }) {
  const { can } = useAuth();
  const to = iso(new Date());
  const from = iso(addDays(new Date(), -13));
  const acts = useAsync(
    () => (can("activities.read") ? api.get<Activity[]>(`/orgs/${orgId}/athletes/${athleteId}/activities`, { from, to }) : Promise.resolve([])),
    [orgId, athleteId, from],
  );
  if (!can("activities.read")) return null;
  const list = acts.data ?? [];
  const total = list.reduce((a, x) => a + (x.distance_m ?? 0), 0);
  return (
    <div style={{ marginTop: 16 }}>
      <h3 className="h-sub">{t("Últimos 14 dias")}</h3>
      {acts.loading && <Loading />}
      {acts.data && (
        <p>
          {list.length === 0
            ? t("Nenhuma atividade importada.")
            : t("{activities}, {dist}; {linked}.", { activities: tn("{n} atividade", "{n} atividades", list.length), dist: metersToKm(total), linked: tn("{n} ligada a treino", "{n} ligadas a treinos", list.filter((a) => a.planned_workout).length) })}{" "}
          <Link to={`/org/${orgId}/atletas/${athleteId}/atividades`}>{t("Ver atividades")}</Link> ·{" "}
          <Link to={`/org/${orgId}/atletas/${athleteId}/evolucao`}>{t("Ver evolução, alertas e metas")}</Link>
        </p>
      )}
    </div>
  );
}

function PlanSummary({ orgId, athleteId, canPlan }: { orgId: string; athleteId: string; canPlan: boolean }) {
  const plan = useAsync(
    () => (canPlan ? api.get<TrainingPlan>(`/orgs/${orgId}/athletes/${athleteId}/plan`) : Promise.resolve(null)),
    [orgId, athleteId, canPlan],
  );
  return (
    <section className="panel" aria-labelledby="planilha">
      <div className="panel-head">
        <h2 id="planilha">{t("Planilha")}</h2>
        {canPlan && (
          <Link to={`/org/${orgId}/atletas/${athleteId}/planilha`} className="btn btn-primary btn-sm">
            {t("Abrir planilha")}
          </Link>
        )}
      </div>
      <div className="panel-body">
        {!canPlan && <p className="muted">{t("A planilha é gerenciada pelo treinador responsável.")}</p>}
        {plan.loading && canPlan && <Loading />}
        {plan.data && (
          <dl className="facts">
            <dt>{t("Versão publicada")}</dt>
            <dd>{plan.data.current_version ? t("{current_version}, em {last_published_at}", { current_version: plan.data.current_version, last_published_at: formatDateTime(plan.data.last_published_at) }) : t("Nenhuma")}</dd>
            <dt>{t("Alterações pendentes")}</dt>
            <dd>{plan.data.pending_changes || t("Nenhuma")}</dd>
            <dt>{t("Ciclo atual")}</dt>
            <dd>
              {plan.data.cycles.find((c) => c.start_date <= todayIso() && todayIso() <= c.end_date)?.name ?? "—"}
            </dd>
          </dl>
        )}
        <RecentActivities orgId={orgId} athleteId={athleteId} />
      </div>
    </section>
  );
}

export function AthleteDetailPage({ orgId, athleteId }: { orgId: string; athleteId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.get<AthleteDetail>(`/orgs/${orgId}/athletes/${athleteId}`), [orgId, athleteId]);
  const [athlete, setAthlete] = useState<AthleteDetail | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingRecord, setEditingRecord] = useState(false);
  const [assigningGroup, setAssigningGroup] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => setAthlete(state.data), [state.data]);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#conversa" && athlete) document.getElementById("conversa")?.scrollIntoView({ block: "start" });
  }, [hash, athlete]);

  if (state.loading && !athlete) return <Loading />;
  if (state.error) {
    return (
      <>
        <PageHead title={t("Atleta")} />
        <ErrorState error={state.error} onRetry={state.error.status === 404 ? undefined : state.reload} />
        <Link to={`/org/${orgId}/atletas`}>{t("Voltar para atletas")}</Link>
      </>
    );
  }
  if (!athlete) return null;

  const canManage = can("athletes.manage");
  const canEditProfile = can("athletes.profile.update");

  async function sendInvite() {
    setInviting(true);
    try {
      setAthlete(await api.post<AthleteDetail>(`/orgs/${orgId}/athletes/${athleteId}/invite`));
      toast(t("Convite enviado."));
    } catch (err) {
      toast((err as ApiError).message, "error");
    } finally {
      setInviting(false);
    }
  }

  return (
    <>
      <p style={{ marginBottom: 8 }}>
        <Link to={`/org/${orgId}/atletas`}>{t("Atletas")}</Link>
      </p>
      <PageHead
        title={athlete.full_name}
        subtitle={
          <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <StatusBadge status={athlete.status} />
            {athlete.has_login ? <Badge tone="ok">{t("Com login")}</Badge> : athlete.invitation_pending ? <Badge tone="warn">{t("Convite enviado")}</Badge> : <Badge>{t("Sem login")}</Badge>}
          </span>
        }
        actions={
          canManage && (
            <>
              {!athlete.has_login && athlete.email && athlete.status !== "inactive" && (
                <Button onClick={sendInvite} busy={inviting}>
                  {athlete.invitation_pending ? t("Reenviar convite") : t("Enviar convite")}
                </Button>
              )}
              <Button variant="primary" onClick={() => setEditingRecord(true)}>
                {t("Editar cadastro")}
              </Button>
            </>
          )
        }
      />

      <div className="split">
        <section className="panel" aria-labelledby="cad">
          <div className="panel-head">
            <h2 id="cad">{t("Cadastro")}</h2>
          </div>
          <div className="panel-body">
            <dl className="facts">
              <dt>{t("E-mail")}</dt>
              <dd>{athlete.email ?? "—"}</dd>
              <dt>{t("Telefone")}</dt>
              <dd>{athlete.phone ?? "—"}</dd>
              <dt>{t("Nascimento")}</dt>
              <dd>
                {formatDate(athlete.birth_date)}
                {athlete.age !== null && <span className="muted"> ({tn("{n} ano", "{n} anos", athlete.age)})</span>}
              </dd>
              <dt>{t("Ingresso")}</dt>
              <dd>{formatDate(athlete.joined_at)}</dd>
              <dt>{t("Treinador")}</dt>
              <dd>{athlete.coach?.name ?? t("Sem treinador")}</dd>
              <dt>{t("Grupo")}</dt>
              <dd>
                {athlete.group?.name ?? "—"}
                {can("groups.members") && (
                  <Button size="sm" variant="ghost" onClick={() => setAssigningGroup(true)}>
                    {athlete.group ? t("Trocar") : t("Atribuir")}
                  </Button>
                )}
              </dd>
              <dt>{t("Atualizado em")}</dt>
              <dd>{formatDateTime(athlete.updated_at)}</dd>
            </dl>
          </div>
        </section>

        <PlanSummary orgId={orgId} athleteId={athleteId} canPlan={can("plans.manage")} />
      </div>

      {can("messages.staff") && (
        <section className="panel section" id="conversa" aria-labelledby="conversa-titulo">
          <div className="panel-head">
            <h2 id="conversa-titulo">{t("Conversa com o atleta")}</h2>
          </div>
          <div className="panel-body">
            <ConversationPanel
              key={athleteId}
              orgId={orgId}
              athleteId={athleteId}
              empty={t("Nenhuma mensagem ainda. Escreva para o atleta: ele recebe um aviso no app.")}
            />
          </div>
        </section>
      )}

      <section className="panel section" aria-labelledby="perfil">
        <div className="panel-head">
          <h2 id="perfil">{t("Perfil esportivo")}</h2>
          {canEditProfile && !editingProfile && <Button onClick={() => setEditingProfile(true)}>{t("Editar perfil")}</Button>}
        </div>
        <div className="panel-body">
          {editingProfile ? (
            <ProfileForm
              mode="full"
              profile={athlete.profile}
              onCancel={() => setEditingProfile(false)}
              onSubmit={async (payload) => {
                setAthlete(await api.patch<AthleteDetail>(`/orgs/${orgId}/athletes/${athleteId}/profile`, payload));
                setEditingProfile(false);
                toast(t("Perfil esportivo salvo."));
              }}
            />
          ) : (
            <ProfileSummary profile={athlete.profile} />
          )}
        </div>
      </section>

      {can("groups.members") && (
        <AssignGroupModal
          open={assigningGroup}
          orgId={orgId}
          athlete={athlete}
          onClose={() => setAssigningGroup(false)}
          onSaved={(a) => {
            setAthlete(a);
            setAssigningGroup(false);
            toast(a.group ? t("Atleta atribuído ao grupo {name}.", { name: a.group.name }) : t("Atleta removido do grupo."));
          }}
        />
      )}
      {canManage && (
        <EditRecordModal
          open={editingRecord}
          orgId={orgId}
          athlete={athlete}
          onClose={() => setEditingRecord(false)}
          onSaved={(a) => {
            setAthlete(a);
            setEditingRecord(false);
            toast(t("Cadastro atualizado."));
          }}
        />
      )}
    </>
  );
}

function EditRecordModal({
  open,
  orgId,
  athlete,
  onClose,
  onSaved,
}: {
  open: boolean;
  orgId: string;
  athlete: AthleteDetail;
  onClose: () => void;
  onSaved: (a: AthleteDetail) => void;
}) {
  const coaches = useAsync(() => (open ? api.get<Coach[]>(`/orgs/${orgId}/coaches`) : Promise.resolve([])), [orgId, open]);
  const groups = useAsync(() => (open ? api.get<Group[]>(`/orgs/${orgId}/groups`) : Promise.resolve([])), [orgId, open]);
  const initial = () => ({
    full_name: athlete.full_name,
    email: athlete.email ?? "",
    phone: athlete.phone ?? "",
    birth_date: athlete.birth_date ?? "",
    joined_at: athlete.joined_at,
    coach_id: athlete.coach?.id ?? "",
    group_id: athlete.group?.id ?? "",
    status: athlete.status as string,
  });
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    if (open) {
      setForm(initial());
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k: keyof ReturnType<typeof initial>) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = {
        ...form,
        email: form.email || null,
        phone: form.phone || null,
        birth_date: form.birth_date || null,
        coach_id: form.coach_id || null,
        group_id: form.group_id || null,
      };
      onSaved(await api.patch<AthleteDetail>(`/orgs/${orgId}/athletes/${athlete.id}`, body));
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const f = error?.fieldErrors() ?? {};
  return (
    <Modal
      open={open}
      title={t("Editar cadastro")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" type="submit" form="edit-athlete" busy={busy}>
            {t("Salvar alterações")}
          </Button>
        </>
      }
    >
      <form id="edit-athlete" className="form" onSubmit={submit} noValidate>
        {error && error.code !== "validation_error" && <FormError error={error} />}
        <TextField label={t("Nome completo")} value={form.full_name} onChange={set("full_name")} error={f.full_name} />
        <div className="form-grid">
          <TextField label={t("E-mail")} type="email" value={form.email} onChange={set("email")} error={f.email} />
          <TextField label={t("Telefone")} type="tel" value={form.phone} onChange={set("phone")} error={f.phone} />
          <TextField label={t("Data de nascimento")} type="date" value={form.birth_date} onChange={set("birth_date")} error={f.birth_date} />
          <TextField label={t("Data de ingresso")} type="date" value={form.joined_at} onChange={set("joined_at")} error={f.joined_at} />
          <SelectField label={t("Treinador responsável")} value={form.coach_id} onChange={set("coach_id")} error={f.coach_id}>
            <option value="">{t("Sem treinador")}</option>
            {coaches.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
              </option>
            ))}
          </SelectField>
          <SelectField label={t("Grupo")} value={form.group_id} onChange={set("group_id")} error={f.group_id}>
            <option value="">{t("Sem grupo")}</option>
            {groups.data?.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t("Situação")}
            value={form.status}
            onChange={set("status")}
            hint={form.status === "inactive" ? t("Atleta inativo perde o acesso à plataforma; o histórico é mantido.") : undefined}
          >
            <option value="active">{t("Ativo")}</option>
            <option value="paused">{t("Pausado")}</option>
            <option value="inactive">{t("Inativo")}</option>
          </SelectField>
        </div>
      </form>
    </Modal>
  );
}

function AssignGroupModal({
  open,
  orgId,
  athlete,
  onClose,
  onSaved,
}: {
  open: boolean;
  orgId: string;
  athlete: AthleteDetail;
  onClose: () => void;
  onSaved: (a: AthleteDetail) => void;
}) {
  const groups = useAsync(() => (open ? api.get<Group[]>(`/orgs/${orgId}/groups`) : Promise.resolve([])), [orgId, open]);
  const { membership } = useAuth();
  const [groupId, setGroupId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    if (open) {
      setGroupId(athlete.group?.id ?? "");
      setError(null);
    }
  }, [open, athlete.group?.id]);
  const isManager = membership?.roles.includes("org_manager");
  // treinador só atribui a grupos que coordena
  const options = (groups.data ?? []).filter((g) => isManager || g.coach?.id === membership?.coach_id);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.put<AthleteDetail>(`/orgs/${orgId}/athletes/${athlete.id}/group`, { group_id: groupId || null }));
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={t("Grupo do atleta")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" onClick={save} busy={busy}>
            {t("Salvar")}
          </Button>
        </>
      }
    >
      {error && <FormError error={error} />}
      <SelectField
        label={t("Grupo")}
        value={groupId}
        onChange={(e) => setGroupId(e.target.value)}
        hint={options.length === 0 && !groups.loading ? t("Nenhum grupo disponível. Crie um grupo em Grupos.") : undefined}
      >
        <option value="">{t("Sem grupo")}</option>
        {options.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </SelectField>
    </Modal>
  );
}
